/**
 * AIReorderDialog — modal para "Ordenar con IA" la Lista Personalizada de un
 * evento a partir de 2-4 eventos pasados como referencia.
 *
 * Flujo (3 pasos):
 *   1) Seleccionar referencias: lista de eventos con playlist ordenada y
 *      fecha pasada. Requiere 2-4 marcados.
 *   2) Loading: llamada al backend `POST /reorder-ai/analyze`.
 *   3) Preview: comparativa "Antes" (izq) vs "Después" (der) con badges de
 *      rol (momento/energía/género). Botones: Aplicar / Ajustar manualmente /
 *      Descartar. En "Ajustar manualmente" el usuario reordena la columna
 *      derecha con drag&drop antes de aplicar.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Loader2, Sparkles, ArrowRight, GripVertical, ChevronUp, ChevronDown, X, CheckCircle2, Wand2, Save, Bookmark, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import axios from 'axios';
import { Input } from './ui/input';

const ENERGY_STYLE = {
  Baja: 'bg-slate-600/40 text-slate-200 border-slate-500/60',
  Media: 'bg-cyan-600/30 text-cyan-200 border-cyan-500/60',
  Alta: 'bg-amber-600/30 text-amber-200 border-amber-500/60',
  'Muy Alta': 'bg-rose-600/30 text-rose-200 border-rose-500/60',
};

const RowBadges = ({ row }) => (
  <div className="flex flex-wrap gap-1 mt-1">
    {row.momento && (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-500/50 text-purple-200 bg-purple-500/10">
        {row.momento}
      </Badge>
    )}
    {row.energia && (
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${ENERGY_STYLE[row.energia] || 'bg-slate-600/30 text-slate-200 border-slate-500/60'}`}>
        {row.energia}
      </Badge>
    )}
    {row.genero && (
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/50 text-emerald-200 bg-emerald-500/10">
        {row.genero}
      </Badge>
    )}
  </div>
);

const SongRow = ({ row, index, moved, note }) => (
  <div
    className={`flex items-start gap-2 rounded-md border p-2 ${
      moved ? 'border-cyan-500/60 bg-cyan-500/5' : 'border-slate-700 bg-slate-800/60'
    }`}
    data-testid={`reorder-row-${row.id}`}
  >
    <span className="text-slate-500 text-xs w-6 shrink-0 mt-0.5 text-right tabular-nums">{index + 1}.</span>
    <div className="min-w-0 flex-1">
      <div className="text-white text-sm font-medium truncate">{row.titulo || 'Sin título'}</div>
      <div className="text-slate-400 text-xs truncate">{row.artista || ''}</div>
      <RowBadges row={row} />
      {note && (
        <div className="mt-1 text-[11px] italic text-cyan-300/80">🎯 {note}</div>
      )}
    </div>
  </div>
);

const ManualRow = ({ row, index, total, onUp, onDown }) => (
  <div className="flex items-start gap-2 rounded-md border border-slate-700 bg-slate-800/60 p-2">
    <span className="text-slate-500 text-xs w-6 shrink-0 mt-0.5 text-right tabular-nums">{index + 1}.</span>
    <div className="min-w-0 flex-1">
      <div className="text-white text-sm font-medium truncate">{row.titulo || 'Sin título'}</div>
      <div className="text-slate-400 text-xs truncate">{row.artista || ''}</div>
      <RowBadges row={row} />
    </div>
    <div className="flex flex-col gap-1">
      <button
        onClick={onUp}
        disabled={index === 0}
        className="p-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`reorder-up-${row.id}`}
        title="Subir"
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onDown}
        disabled={index === total - 1}
        className="p-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`reorder-down-${row.id}`}
        title="Bajar"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

export const AIReorderDialog = ({ open, onOpenChange, playlistId, onApplied }) => {
  const [step, setStep] = useState('select'); // 'select' | 'loading' | 'preview' | 'manual'
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null); // {before, after, pattern_summary, ...}
  const [manualOrder, setManualOrder] = useState([]); // array of rows (edited copy of "after")
  const [applying, setApplying] = useState(false);
  // Plantillas (patrones reusables)
  const [patterns, setPatterns] = useState([]);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savingPattern, setSavingPattern] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const pollAbortRef = React.useRef(false);

  // Reset on open
  useEffect(() => {
    if (!open) {
      pollAbortRef.current = true; // aborta polling si el modal se cierra
      return;
    }
    pollAbortRef.current = false;
    setStep('select');
    setResult(null);
    setSelected(new Set());
    setManualOrder([]);
    setShowSaveInput(false);
    setSaveName('');
    setLoadingStage('');
    fetchCandidates();
    fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchCandidates = async () => {
    if (!playlistId) return;
    setLoadingCandidates(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API_URL}/playlists/${playlistId}/reorder-ai/reference-candidates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCandidates(r.data?.candidates || []);
    } catch (e) {
      console.error('[AIReorder] fetchCandidates', e);
      toast.error('Error cargando eventos de referencia');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchPatterns = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/reorder-patterns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatterns(r.data?.patterns || []);
    } catch (e) {
      console.error('[AIReorder] fetchPatterns', e);
    }
  };

  const runApplyPattern = async (patternId) => {
    setStep('loading');
    setLoadingStage('Encolando trabajo…');
    try {
      const token = localStorage.getItem('token');
      const start = await axios.post(
        `${API_URL}/playlists/${playlistId}/reorder-ai/apply-pattern/start`,
        { pattern_id: patternId },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error('No se recibió job_id');
      const data = await pollJob(jobId);
      setResult(data);
      setManualOrder(data.after || []);
      setStep('preview');
    } catch (e) {
      if (e.message === 'cancelled') return;
      console.error('[AIReorder] applyPattern', e);
      const msg = e.response?.data?.detail || e.message || 'La IA no pudo aplicar la plantilla';
      toast.error(msg);
      setStep('select');
    }
  };

  const deletePattern = async (patternId, name) => {
    if (!window.confirm(`¿Borrar la plantilla "${name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/reorder-patterns/${patternId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Plantilla borrada');
      fetchPatterns();
    } catch (e) {
      toast.error('Error borrando plantilla');
    }
  };

  const savePatternNow = async (rows) => {
    const name = (saveName || '').trim();
    if (!name) {
      toast.info('Escribe un nombre para la plantilla');
      return;
    }
    setSavingPattern(true);
    try {
      const token = localStorage.getItem('token');
      const orderedIds = rows.map((r) => r.id);
      await axios.post(
        `${API_URL}/reorder-patterns`,
        {
          name,
          ordered_song_ids: orderedIds,
          pattern_summary: result?.pattern_summary || null,
          source_event_id: playlistId,
          source_reference_event_ids: result?.reference_event_ids || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plantilla "${name}" guardada`);
      setSaveName('');
      setShowSaveInput(false);
      fetchPatterns();
    } catch (e) {
      console.error('[AIReorder] savePattern', e);
      toast.error(e.response?.data?.detail || 'Error guardando plantilla');
    } finally {
      setSavingPattern(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 4) {
          toast.info('Máximo 4 eventos de referencia');
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const canAnalyze = selected.size >= 2 && selected.size <= 4;

  // Polling de jobs async — resistente a timeouts del edge en producción.
  const pollJob = async (jobId) => {
    const token = localStorage.getItem('token');
    const maxAttempts = 90; // ~3 min a 2s por poll
    for (let i = 0; i < maxAttempts; i++) {
      if (pollAbortRef.current) throw new Error('cancelled');
      await new Promise((r) => setTimeout(r, i < 3 ? 1500 : 2500));
      try {
        const r = await axios.get(
          `${API_URL}/playlists/${playlistId}/reorder-ai/status/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
        );
        const d = r.data || {};
        if (d.status === 'running') {
          setLoadingStage('La IA está trabajando…');
        } else if (d.status === 'pending') {
          setLoadingStage('En cola…');
        }
        if (d.status === 'done') return d.result;
        if (d.status === 'failed') {
          const err = new Error(d.error || 'La IA falló');
          err.status = d.status_code;
          throw err;
        }
      } catch (e) {
        // Errores transitorios de red: seguimos polling; si es 404 abortamos.
        if (e.response?.status === 404) throw e;
        if (e.message === 'cancelled') throw e;
        if (e.status) throw e; // job.failed → propaga
      }
    }
    throw new Error('Tiempo de espera agotado (3 min)');
  };

  const runAnalyze = async () => {
    if (!canAnalyze) return;
    setStep('loading');
    setLoadingStage('Encolando trabajo…');
    try {
      const token = localStorage.getItem('token');
      const start = await axios.post(
        `${API_URL}/playlists/${playlistId}/reorder-ai/analyze/start`,
        { reference_event_ids: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error('No se recibió job_id');
      const data = await pollJob(jobId);
      setResult(data);
      setManualOrder(data.after || []);
      setStep('preview');
    } catch (e) {
      if (e.message === 'cancelled') return;
      console.error('[AIReorder] analyze', e);
      const msg = e.response?.data?.detail || e.message || 'La IA no pudo procesar el orden';
      toast.error(msg);
      setStep('select');
    }
  };

  // Detecta si una fila cambió de posición respecto al orden original.
  const beforePosById = useMemo(() => {
    const map = new Map();
    (result?.before || []).forEach((r, i) => map.set(r.id, i));
    return map;
  }, [result]);

  const applyOrder = async (finalRows) => {
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const songIds = finalRows.map((r) => r.id);
      await axios.post(
        `${API_URL}/playlists/${playlistId}/save-custom-list`,
        { song_ids: songIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Playlist reordenada (${songIds.length} canciones)`);
      onApplied?.(songIds);
      onOpenChange?.(false);
    } catch (e) {
      console.error('[AIReorder] apply', e);
      toast.error('Error al guardar el nuevo orden');
    } finally {
      setApplying(false);
    }
  };

  const moveManual = (idx, dir) => {
    setManualOrder((rows) => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const copy = rows.slice();
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl bg-slate-900 border-slate-700 text-white overflow-hidden"
        data-testid="ai-reorder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            Ordenar con IA por patrones
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            La IA analiza el orden de 2-4 eventos pasados y reordena esta playlist emparejando cada canción por su ROL (momento + energía + género).
          </DialogDescription>
        </DialogHeader>

        {/* Step: SELECT references */}
        {step === 'select' && (
          <div className="space-y-3" data-testid="reorder-step-select">
            {/* Sección plantillas guardadas (aparece sólo si hay alguna) */}
            {patterns.length > 0 && (
              <div className="rounded-md border border-purple-500/40 bg-purple-500/5 p-3 space-y-2" data-testid="saved-patterns-section">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-200">
                  <Bookmark className="w-4 h-4" />
                  Plantillas guardadas
                </div>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                  {patterns.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 rounded border border-slate-700 bg-slate-800/60 p-2"
                      data-testid={`saved-pattern-${p.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-medium truncate">{p.name}</div>
                        <div className="text-slate-400 text-xs truncate">
                          {p.size} slots · {p.created_at?.slice(0, 10)}
                          {p.pattern_summary ? ` · ${p.pattern_summary.slice(0, 60)}…` : ''}
                        </div>
                      </div>
                      <Button
                        onClick={() => runApplyPattern(p.id)}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-500 text-white h-8"
                        data-testid={`apply-pattern-${p.id}-btn`}
                      >
                        <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                        Aplicar
                      </Button>
                      <Button
                        onClick={() => deletePattern(p.id, p.name)}
                        size="icon"
                        variant="outline"
                        className="border-rose-500/40 bg-rose-950/30 text-rose-300 hover:bg-rose-900/50 h-8 w-8"
                        data-testid={`delete-pattern-${p.id}-btn`}
                        title="Borrar plantilla"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500">
                  O selecciona eventos de referencia abajo para crear un orden nuevo.
                </div>
              </div>
            )}

            <div className="text-sm text-slate-300">
              Selecciona entre 2 y 4 eventos como referencia (solo eventos pasados con playlist ≥10 canciones):
            </div>
            {loadingCandidates ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando eventos…
              </div>
            ) : candidates.length === 0 ? (
              <div className="rounded-md border border-slate-700 bg-slate-800/50 p-6 text-center text-slate-400">
                No hay eventos elegibles como referencia. Necesitas al menos 2 eventos pasados con playlist ordenada ≥10 canciones.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2 pr-1">
                {candidates.map((c) => {
                  const isSel = selected.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
                        isSel ? 'border-cyan-500/70 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                      }`}
                      data-testid={`reorder-candidate-${c.id}`}
                    >
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggleSelect(c.id)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-semibold truncate">{c.nombre_novios}</div>
                        <div className="text-slate-400 text-xs">{c.fecha_evento} · {c.total_canciones} canciones</div>
                        {c.lugar_evento && (
                          <div className="text-slate-500 text-xs truncate">{c.lugar_evento}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <div className="text-xs text-slate-400" data-testid="reorder-selected-count">
                Seleccionados: {selected.size}/4 (mínimo 2)
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange?.(false)} className="border-slate-600 text-slate-200 hover:bg-slate-800">
                  Cancelar
                </Button>
                <Button
                  onClick={runAnalyze}
                  disabled={!canAnalyze}
                  className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white disabled:opacity-40"
                  data-testid="reorder-analyze-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Analizar y proponer orden
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step: LOADING */}
        {step === 'loading' && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-300" data-testid="reorder-step-loading">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
            <div className="font-semibold">La IA está analizando los patrones…</div>
            <div className="text-xs text-cyan-300 min-h-[16px]" data-testid="reorder-loading-stage">{loadingStage || 'Preparando…'}</div>
            <div className="text-xs text-slate-500">Suele tardar entre 20 y 60 segundos. No cierres la ventana.</div>
          </div>
        )}

        {/* Step: PREVIEW (before / after) */}
        {step === 'preview' && result && (
          <div className="space-y-3" data-testid="reorder-step-preview">
            <div className="rounded-md border border-cyan-500/40 bg-cyan-500/5 p-3 text-sm">
              <span className="text-cyan-300 font-semibold">Patrón detectado: </span>
              <span className="text-slate-200">{result.pattern_summary || 'Ver comparativa.'}</span>
              <div className="mt-1 text-xs text-slate-400">
                {result.changed_positions} de {result.total} canciones cambian de posición · modelo {result.model}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[440px] overflow-y-auto">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">Antes</div>
                <div className="space-y-1.5">
                  {(result.before || []).map((row, i) => (
                    <SongRow key={`b-${row.id}-${i}`} row={row} index={i} moved={false} />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-cyan-300 mb-1.5 flex items-center gap-1">
                  Después <ArrowRight className="w-3 h-3" />
                </div>
                <div className="space-y-1.5">
                  {(result.after || []).map((row, i) => {
                    const originalPos = beforePosById.get(row.id);
                    const moved = originalPos !== i;
                    return (
                      <SongRow
                        key={`a-${row.id}-${i}`}
                        row={row}
                        index={i}
                        moved={moved}
                        note={row.role_note}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={applying || savingPattern}
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
                data-testid="reorder-discard-btn"
              >
                <X className="w-4 h-4 mr-2" /> Descartar
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSaveInput((v) => !v)}
                disabled={applying || savingPattern}
                className="border-purple-500/50 bg-purple-950/30 text-purple-200 hover:bg-purple-900/40"
                data-testid="reorder-save-pattern-toggle-btn"
              >
                <Save className="w-4 h-4 mr-2" /> Guardar como plantilla
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep('manual')}
                disabled={applying || savingPattern}
                className="border-cyan-500/50 bg-cyan-950/30 text-cyan-200 hover:bg-cyan-900/40"
                data-testid="reorder-manual-btn"
              >
                <GripVertical className="w-4 h-4 mr-2" /> Ajustar manualmente
              </Button>
              <Button
                onClick={() => applyOrder(result.after)}
                disabled={applying || savingPattern}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="reorder-apply-btn"
              >
                {applying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</>)
                          : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Aplicar orden IA</>)}
              </Button>
            </DialogFooter>
            {showSaveInput && (
              <div className="mt-2 flex items-center gap-2 rounded-md border border-purple-500/40 bg-purple-500/5 p-2" data-testid="save-pattern-input-row">
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder='Nombre (ej. "Boda 40 años pop&rock")'
                  className="bg-slate-800 border-slate-600 text-white h-9"
                  data-testid="save-pattern-name-input"
                />
                <Button
                  onClick={() => savePatternNow(result.after)}
                  disabled={savingPattern || !saveName.trim()}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-500 text-white"
                  data-testid="save-pattern-confirm-btn"
                >
                  {savingPattern ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Guardar
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step: MANUAL adjust */}
        {step === 'manual' && (
          <div className="space-y-3" data-testid="reorder-step-manual">
            <div className="text-sm text-slate-300">
              Ajusta el orden manualmente con las flechas ↑/↓ y luego aplica.
            </div>
            <div className="max-h-[440px] overflow-y-auto space-y-1.5 pr-1">
              {manualOrder.map((row, i) => (
                <ManualRow
                  key={`m-${row.id}-${i}`}
                  row={row}
                  index={i}
                  total={manualOrder.length}
                  onUp={() => moveManual(i, -1)}
                  onDown={() => moveManual(i, +1)}
                />
              ))}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setStep('preview')}
                disabled={applying || savingPattern}
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
                data-testid="reorder-back-preview-btn"
              >
                Volver a la comparativa
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSaveInput((v) => !v)}
                disabled={applying || savingPattern}
                className="border-purple-500/50 bg-purple-950/30 text-purple-200 hover:bg-purple-900/40"
                data-testid="reorder-save-pattern-manual-toggle-btn"
              >
                <Save className="w-4 h-4 mr-2" /> Guardar como plantilla
              </Button>
              <Button
                onClick={() => applyOrder(manualOrder)}
                disabled={applying || savingPattern}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="reorder-apply-manual-btn"
              >
                {applying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</>)
                          : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Aplicar orden ajustado</>)}
              </Button>
            </DialogFooter>
            {showSaveInput && (
              <div className="mt-2 flex items-center gap-2 rounded-md border border-purple-500/40 bg-purple-500/5 p-2">
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder='Nombre (ej. "Boda 40 años pop&rock")'
                  className="bg-slate-800 border-slate-600 text-white h-9"
                  data-testid="save-pattern-manual-name-input"
                />
                <Button
                  onClick={() => savePatternNow(manualOrder)}
                  disabled={savingPattern || !saveName.trim()}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-500 text-white"
                  data-testid="save-pattern-manual-confirm-btn"
                >
                  {savingPattern ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Guardar
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIReorderDialog;
